/**
 * Mobile Chat View
 *
 * Full-screen chat experience for mobile devices. Messages scroll full-height,
 * with a floating translucent pill input at the bottom.
 *
 * Reuses the same ChatSyncContext for message state and streaming, so
 * messages stay in sync with the desktop drawer and pop-out window.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Sparkles, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useChatSyncContext } from '../contexts/ChatSyncContext';
import { MarkdownContent, StreamingMarkdownContent } from './MarkdownContent';
import { ChatCardRenderer } from './ChatCardRenderer';
import { VoiceButton } from './VoiceButton';
import type { ChatPageContext } from '../api/client';
import type { ChatCard } from '../types';

interface MobileChatViewProps {
  onResponse?: () => void;
  context?: ChatPageContext;
}

// True on devices with a touch screen (phones, tablets) — false on desktop browsers
// even when rendered at narrow widths (e.g., Chrome extension side panel).
const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

export function MobileChatView({ onResponse, context }: MobileChatViewProps) {
  const [input, setInput] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    () => window.visualViewport?.height ?? window.innerHeight
  );
  const baselineViewportHeightRef = useRef(viewportHeight);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    streamingContent,
    toolStatus,
    sendMessageStream,
    setChatContext,
  } = useChatSyncContext();

  useEffect(() => {
    setChatContext(context);
  }, [context, setChatContext]);

  // Honor preload requests from the Getting-started checklist (and any other
  // surface): a `danny:chat-input:set` event pre-fills this composer with a
  // starter prompt. Mirrors ChatPanel so the mobile chat surface behaves the
  // same as the desktop drawer — otherwise the checklist "Try it" CTA opens an
  // empty chat and the suggested prompt is silently dropped.
  useEffect(() => {
    const onSet = (e: Event) => {
      const next = (e as CustomEvent<string>).detail;
      if (typeof next !== 'string') return;
      setInput(next);
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          ta.setSelectionRange(next.length, next.length);
        }
      });
    };
    window.addEventListener('danny:chat-input:set', onSet);
    return () => window.removeEventListener('danny:chat-input:set', onSet);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const frameId = requestAnimationFrame(() => {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    });
    return () => cancelAnimationFrame(frameId);
  }, [input]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateViewport = () => {
      const nextHeight = window.visualViewport?.height ?? window.innerHeight;
      setViewportHeight(nextHeight);

      if (!isInputFocused) {
        baselineViewportHeightRef.current = nextHeight;
      }
    };

    updateViewport();

    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    visualViewport.addEventListener('resize', updateViewport);

    return () => {
      visualViewport.removeEventListener('resize', updateViewport);
    };
  }, [isInputFocused]);

  // Lock document body to position:fixed while keyboard is open.
  // This prevents iOS Safari from scrolling the document to bring
  // the focused textarea into view — eliminating the visible flash
  // that a reactive scroll listener would cause.
  // Only applies on touch devices with on-screen keyboards.
  useEffect(() => {
    if (!isTouchDevice || !isInputFocused) return;

    const { body } = document;
    const prev = {
      position: body.style.position,
      width: body.style.width,
      top: body.style.top,
    };

    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.top = '0';

    return () => {
      body.style.position = prev.position;
      body.style.width = prev.width;
      body.style.top = prev.top;
    };
  }, [isInputFocused]);

  // Belt-and-suspenders: iOS standalone PWA may still scroll the
  // document even with body position:fixed. Pin scroll to 0.
  // Only applies on touch devices.
  useEffect(() => {
    if (!isTouchDevice || !isInputFocused) return;

    const killScroll = () => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    killScroll();
    window.addEventListener('scroll', killScroll, { passive: false });

    return () => {
      window.removeEventListener('scroll', killScroll);
    };
  }, [isInputFocused]);

  const isKeyboardOpen =
    isTouchDevice && isInputFocused && viewportHeight < baselineViewportHeightRef.current - 50;

  const chatShellStyle = isKeyboardOpen ? { height: `${viewportHeight}px` } : undefined;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;

      const message = input.trim();
      setInput('');

      const response = await sendMessageStream(message, context);
      if (response?.success && onResponse) {
        onResponse();
      }
    },
    [input, isLoading, sendMessageStream, context, onResponse]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const handleCardAction = useCallback(
    (action: NonNullable<ChatCard['actions']>[number]) => {
      if (action.actionType === 'share') {
        const text = action.metadata?.text ?? '';
        if (navigator.share) {
          navigator.share({ text }).catch(() => {});
        } else {
          navigator.clipboard.writeText(text).then(() => {
            toast.success('Copied to clipboard!');
          }).catch(() => {});
        }
      } else if (action.url) {
        window.open(action.url, '_blank', 'noopener,noreferrer');
      }
    },
    []
  );

  const dismissKeyboardButton = (
    <button
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => textareaRef.current?.blur()}
      className={clsx(
        'z-40 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-surface-base-dark/80 backdrop-blur-sm flex items-center justify-center shadow-md',
        isKeyboardOpen ? 'absolute top-0 -translate-y-full' : 'fixed'
      )}
      style={
        isKeyboardOpen ? undefined : { bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }
      }
      aria-label="Dismiss keyboard"
    >
      <ChevronDown className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
    </button>
  );

  return (
    <div
      data-testid="mobile-chat-shell"
      className={clsx(
        'flex flex-col overflow-hidden bg-surface-base-light dark:bg-surface-base-dark',
        isKeyboardOpen ? 'fixed inset-0' : 'relative h-full'
      )}
      style={chatShellStyle}
    >
      {/* Messages — scrolls under the frosted MobileTopBar.
          3.5rem clears the bar icons (~48px); env() adds notch clearance.
          Keep this in sync with DetailPanelLayout's scroller inset. */}
      <div
        ref={chatContainerRef}
        data-testid="mobile-chat-scroller"
        className="flex-1 overflow-y-auto px-3 space-y-2 min-h-0"
        style={{
          paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
          paddingBottom: isKeyboardOpen
            ? '1rem'
            : 'calc(8rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Your AI task assistant
            </p>
            {/* Suggestion chips — showcase Danny's unique features */}
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {[
                "Plan my day",
                "Break down a task",
                "Draft an email",
                "What's on my calendar?",
                "Show overdue tasks",
                "Search my tasks",
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInput(suggestion)}
                  className="px-3 py-1.5 text-sm border border-border-subtle-light dark:border-border-subtle-dark rounded-full text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={clsx('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={clsx(
                'max-w-[85%] rounded-lg text-sm',
                msg.role === 'user'
                  ? 'px-3 py-1.5 bg-primary-700 text-white'
                  : 'px-3 py-1 bg-surface-base-light/50 dark:bg-surface-hover-dark/50 text-text-primary-light dark:text-text-primary-dark'
              )}
            >
              {msg.role === 'user' ? (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              ) : (
                <MarkdownContent content={msg.content} />
              )}
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border-subtle-light/20 dark:border-border-hover-dark/20 space-y-1">
                  {msg.actions.map((action, i) => (
                    <p key={i} className="text-xs opacity-75">
                      {action.description}
                    </p>
                  ))}
                </div>
              )}
              {msg.cards && msg.cards.map((card, i) => (
                <ChatCardRenderer key={i} card={card} onAction={handleCardAction} />
              ))}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-base-light/50 dark:bg-surface-hover-dark/50 rounded-lg px-3 py-1 text-sm text-text-primary-light dark:text-text-primary-dark">
              {toolStatus ? (
                <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{toolStatus}</span>
                </div>
              ) : streamingContent ? (
                <StreamingMarkdownContent content={streamingContent} />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin text-text-secondary-light dark:text-text-secondary-dark" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dismiss keyboard button — only on touch devices with on-screen keyboards */}
      {isTouchDevice && isInputFocused && dismissKeyboardButton}

      {/* Floating input pill */}
      <div
        data-testid="mobile-chat-composer"
        className={clsx(
          'z-30 px-3',
          isKeyboardOpen ? 'relative inset-x-0 shrink-0 pt-2' : 'fixed bottom-0 inset-x-0'
        )}
        style={{
          paddingBottom: isKeyboardOpen
            ? '0.5rem'
            : 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className="bg-surface-base-light/95 dark:bg-surface-base-dark/95 backdrop-blur-md rounded-2xl border shadow-lg transition-all border-border-subtle-light dark:border-border-subtle-dark focus-within:border-primary-300 dark:focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 dark:focus-within:ring-primary-900/50">
            {/* Row 1: Text input */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              placeholder="Ask Danny anything..."
              rows={1}
              className="chat-input w-full min-w-0 py-2.5 px-4 bg-transparent resize-none focus:outline-none text-base text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark"
              disabled={isLoading}
            />
            {/* Row 2: Action bar */}
            <div className="flex items-center justify-between px-2 pb-2">
              <VoiceButton onTranscription={(text) => setInput(text)} disabled={isLoading} />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className={clsx(
                  'shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors',
                  input.trim() && !isLoading
                    ? 'bg-primary-500 text-surface-base-dark'
                    : 'bg-surface-hover-light dark:bg-surface-hover-dark text-text-tertiary-light dark:text-text-tertiary-dark'
                )}
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
